<template>
  <div id="app" :class="langCode" v-if="!loadingLang">
    <login-modal ref="loginModal" />
    <LegalAgreementDocument />
    <router-view></router-view>
  </div>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";
import LegalAgreementDocument from "@/components/LegalAgreementDocument.vue";
import { getCookie } from "@/helpers/cookie.js";
import { adoptSharedSession } from "@/helpers/shared-session";
import { notificationsMixin } from "@/mixins";
import loginModal from "./views/LoginModal.vue";
import { getLangCodeFromLocation } from "./constants/countries";

export default {
  components: {
    loginModal,
    LegalAgreementDocument,
  },

  mixins: [notificationsMixin],

  computed: mapState({
    loadingLang: (state) => {
      return state.translation.loadTranslation;
    },

    langCode: (state) => {
      return state.translation.locate;
    },
  }),

  methods: {
    setLocale(countryCode, lang) {
      this.$store.dispatch("featureHide/setIPBasedFeature", countryCode);
      this.$store.dispatch("setTranslation", lang);
    },
    async onCreated() {
      let lang = getCookie("language") || null;

      await this.$store.dispatch("checkToken");

      this.$store
        .dispatch("initData")
        .then(({ headers }) => {
          // Try to get languages if user is authenticated, otherwise continue with empty array
          const hasJwt = !!localStorage.getItem("jwt_token");
          if (hasJwt) {
            return this.$store
              .dispatch("getLanguages")
              .then((languagesAvailable) => ({ headers, languagesAvailable }))
              .catch((err) => {
                console.warn("Unable to load organization languages", err);
                return { headers, languagesAvailable: [] };
              });
          } else {
            // User not authenticated, skip getLanguages and continue with IP-based detection
            return { headers, languagesAvailable: [] };
          }
        })
        .then(({ headers, languagesAvailable }) => {
          const countryCode = headers["x-geo-ip-country"].toLocaleLowerCase();

          if (lang) return Promise.reject({ countryCode, lang });
          const firstLang = getLangCodeFromLocation(countryCode);

          // 2. set lang on first time
          this.setLocale(countryCode, firstLang);

          return { countryCode, languagesAvailable };
        })
        .then(({ countryCode, languagesAvailable }) => {
          const detectedLang = getLangCodeFromLocation(countryCode);
          const availableLang = languagesAvailable.find(
            ({ short }) => short === detectedLang
          )?.short;
          if (!availableLang && languagesAvailable.length > 0) {
            console.warn(
              `Could not detect language. ${detectedLang.toUpperCase()} not defined for current organization`
            );
          }

          // 3. set lang again for org: apply the geo-detected language only if
          // the organization actually offers it; otherwise fall back to English
          // (when available) or the first available language. This keeps the
          // applied locale within the navbar selector's option list, which is
          // built from the same set — preventing a blank/desynced selector.
          const orgLang =
            availableLang ||
            languagesAvailable.find(({ short }) => short === "en")?.short ||
            languagesAvailable[0]?.short ||
            detectedLang;
          this.setLocale(countryCode, orgLang);
        })
        .catch((e) => {
          if (e instanceof Error) {
            console.error("Error in language initialization:", e);
            // Fallback to IP-based detection even if there was an error
            return this.$store
              .dispatch("initData")
              .then(({ headers }) => {
                const countryCode = headers["x-geo-ip-country"]?.toLocaleLowerCase() || "us";
                const detectedLang = getLangCodeFromLocation(countryCode);
                this.setLocale(countryCode, detectedLang);
              })
              .catch(() => {
                // Ultimate fallback
                this.setLocale("us", "en");
              });
          }
          if (!e?.countryCode && !e?.lang) {
            console.warn("No lang set");
            return;
          }

          // 1. set lang from cookie
          const { countryCode, lang } = e;
          this.setLocale(countryCode, lang);
        });

      this.$store.dispatch("getTranslation");

      this.$store.dispatch("getTimeZones");

      sessionStorage.setItem("show_access_toasts", this.$route.query.show_access_toasts || "0");
      const token =
        this.$route.currentRoute?.params?.token || this.$route.currentRoute?.query?.token;

      axios.interceptors.response.use(
        (response) => {
          return response;
        },
        (error) => {
          const { status, config, data } = error.response || {};

          if (status == 403 || status == 401) {
            const { forbidden } = data || {};

            if (forbidden) {
              const url = new URL(config.url);

              const action = forbidden.replace("Can", "");

              let msg = `${action}`;

              if (sessionStorage.getItem("show_access_toasts") === "1") {
                msg = `${action} - ${url.pathname}`;
              }

              const h = this.$createElement;

              const vNodesDetails = h(
                "div",
                {
                  class: ["text-break"],
                },
                msg
              );

              this.$_notificationsMixin_makeToast("Access forbidden", [vNodesDetails], "warning");
            }

            if (
              !token &&
              !forbidden &&
              this.$route.path.indexOf("login") == -1 &&
              this.$route.path.indexOf("log-in") == -1 &&
              this.$route.path.indexOf("register") == -1 &&
              this.$route.path.indexOf("sign-up") == -1 &&
              this.$route.path.indexOf("resetpassword") == -1 &&
              this.$route.name !== "DonorChooseAccount" &&
              this.$route.name !== "SignIn" &&
              this.$route.name !== "SignInToken"
            ) {
              // The shared cookie may hold a session to carry on with, e.g. a
              // newer token of this login after signing out and back in on the
              // other dashboard (same rules as on boot), so no sign-in needed.
              if (adoptSharedSession(this.$store)) {
                window.location.reload();
                return Promise.reject(error);
              }
              this.$refs.loginModal.openModal();
            }

            if (forbidden === "CanSeeOrganizationDetail") {
              localStorage.removeItem("orgId");
              this.$refs.loginModal.openModal();
            }
          }

          return Promise.reject(error);
        }
      );

      window.onblur = () => {
        this.$store.commit("setBlured", true);
      };
      window.onfocus = () => {
        this.$store.commit("setBlured", false);
      };
    },
  },

  created: function () {
    this.onCreated();
  },
};
</script>

<style lang="scss">
#app {
  font-family: $vito-light;
  background-color: $bg-20;
  min-width: 320px;
  overflow: auto;
}

#loginModalBox .login-inner {
  max-width: 100%;
}

#loginModalBox #login {
  height: 100%;
}

.load-image-input {
  display: none !important;
}
</style>
